import create from 'zustand';
import produce from 'immer';
import VoxeetSDK from '@voxeet/voxeet-web-sdk';
import { request } from '../libs/utils';
import { onSnapshot, getFirestore, collection, doc, query, where } from 'firebase/firestore';

function toParticipantObject(p){
    return {
        id: p.id,
        name: p.info.name,
        avatar: p.info.avatarUrl,
        status: p.status,
        type: p.type,
        audioTransmitting: p.audioTransmitting,
        audioReceivingFrom: p.audioReceivingFrom,
        pesuId: p.info.externalId,
        original: p,
        stream: (p.streams || []).find(s => s.type === 'Camera' && s.getVideoTracks().length > 0),
        screen: (p.streams || []).find(s => s.type === 'ScreenShare' && s.getVideoTracks().length > 0),
    }
}

const useRoomStore = create((set, get) => {
    let roomWatcher = null;
    let messagesWatcher = null;
    let filesWatcher = null;
    return {
        open: async (id) => {
            const room = await (await request(`/api/room/${id}`)).json();
            set(room);
            const db = getFirestore();
            roomWatcher = onSnapshot(doc(db, 'pesu.rooms', room.id), doc => {
                set(state => produce(state, draft => {
                    draft.locked = doc.data().locked;
                    draft.members = doc.data().members;
                    draft.files = doc.data().files;
                    draft.messages = doc.data().messages;
                }));
                get().updateSpatialPositions();
            });
            messagesWatcher = onSnapshot(collection(db, 'pesu.rooms', get().id, 'messages'), coll => {
                set(state => produce(state, draft => {
                    draft.messages = coll.docs.map(d => d.data()).sort((a, b) => {
                        return a.createdOn.toDate() - b.createdOn.toDate();
                    });
                }));
            });
            filesWatcher = onSnapshot(collection(db, 'pesu.rooms', get().id, 'files'), coll => {
                set(state => produce(state, draft => {
                    draft.files = coll.docs.map(d => d.data()).sort((a, b) => {
                        return a.createdOn.toDate() - b.createdOn.toDate();
                    });
                }));
            })
            return room;
        },
        updateSpatialPositions: () => {
            if(get().scale){
                setTimeout(() => {
                    const members = get().members;
                    const participants = get().participants;
                    Object.keys(members || {}).forEach(mid => {
                        const participant = participants.find(p => p.pesuId === mid);
                        if (participant && members[mid].pos) {
                            const pos = members[mid].pos;
                            try{
                                VoxeetSDK.conference.setSpatialPosition(participant.original, { x: pos[0] / 100, y: pos[1] / 100, z: 0 });
                            }
                            catch(ex){}
                        }
                    })
                }, 500);
            }
        },
        join: async ({
            scale = 1
        }) => {
            set({scale});
            try{
                VoxeetSDK.conference.leave();
            }
            catch(ex){}
            const conference = await VoxeetSDK.conference.create({
                alias: get().id,
                params: {
                    liveRecording: false,
                    rtcpMode: "average", // worst, average, max
                    ttl: 0,
                    videoCodec: "H264", // H264, VP8
                    dolbyVoice: true
                }
            });
            console.log('CONFERENCE', conference);
            await VoxeetSDK.conference.join(conference, {
                constraints: {
                    audio: false,
                    video: false
                },
                spatialAudio: !!scale
            });
            if(scale){
                VoxeetSDK.conference.setSpatialEnvironment({
                    x: scale,
                    y: scale,
                    z: 1
                },
                { x: 0, y: -1, z: 0 },
                { x: 0, y: 0,  z: 1 },
                { x: 1, y: 0,  z: 0 });
            }

            set(state => produce(state, draft => {
                draft.participants = [...conference.participants].map(([id, val]) => val).filter(p => !!p).map(toParticipantObject);
            }));

            get().updateSpatialPositions();
            
            VoxeetSDK.conference.on("joined", (participant) => {
                get().updateSpatialPositions();
            });

            VoxeetSDK.conference.on('streamAdded', (participant, stream) => {
                set(produce(state => {
                    const existingParticipant = state.participants.find(p => p.pesuId === participant.info.externalId);
                    if(existingParticipant){
                        if(stream.type === 'Camera' && stream.getVideoTracks().length > 0){
                            existingParticipant.stream = stream;
                        }
                        else if(stream.type === 'ScreenShare' && stream.getVideoTracks().length > 0){
                            existingParticipant.screen = stream;
                        }
                        existingParticipant.updatedOn = new Date();
                    }
                }));
            });
            VoxeetSDK.conference.on('streamRemoved', (participant, stream) => {
                set(produce(state => {
                    const existingParticipant = state.participants.find(p => p.pesuId === participant.info.externalId);
                    if(existingParticipant){
                        if(stream.type === 'Camera'){
                            existingParticipant.stream = null;
                            existingParticipant.hasVideo = false;
                        }
                        else if(stream.type === 'ScreenShare'){
                            existingParticipant.screen = null;
                        }
                        existingParticipant.updatedOn = new Date();
                    }
                }));
            });
            VoxeetSDK.conference.on('streamUpdated', (participant, stream) => {
                set(state => produce(state, draft => {
                    console.log('PARTI', draft.participants, participant);
                    
                    const existingParticipant = draft.participants.find(p => {
                        console.log('PARTI MATCHE', p.pesuId, participant.info.externalId, p.pesuId === participant.info.externalId)
                        return p.pesuId === participant.info.externalId;
                    });
                    if(existingParticipant){
                        if(stream.type === 'Camera' && stream.getVideoTracks().length > 0){
                            existingParticipant.stream = stream;
                        }
                        else if(stream.type === 'ScreenShare'){
                            existingParticipant.screen = stream;
                        }
                        else{
                            existingParticipant.stream = null;
                            existingParticipant.screen = null;
                        }
                        existingParticipant.updatedOn = new Date();
                    }
                }));
            });

            VoxeetSDK.conference.on('participantAdded', (participant) => {
                set(produce(state => {
                    const existingParticipant = state.participants.find(p => p.pesuId === participant.info.externalId);
                    if(existingParticipant){
                        existingParticipant.name = participant.info.name;
                        existingParticipant.avatar = participant.info.avatarUrl;
                        existingParticipant.status = participant.status;
                        existingParticipant.type = participant.type;
                        existingParticipant.audioTransmitting = participant.audioTransmitting;
                        existingParticipant.audioReceivingFrom = participant.audioReceivingFrom;
                        existingParticipant.updatedOn = new Date();
                    }
                    else{
                        state.participants.push(toParticipantObject(participant));
                    }
                }));
                get().updateSpatialPositions(scale);
            });

            VoxeetSDK.conference.on('participantUpdated', (participant) => {
                console.log('PART UPDATED', participant);
                get().updateSpatialPositions(scale);
                set(produce(state => {
                    const existingParticipant = state.participants.find(p => p.pesuId === participant.info.externalId);
                    if(existingParticipant){
                        existingParticipant.name = participant.info.name;
                        existingParticipant.avatar = participant.info.avatarUrl;
                        existingParticipant.status = participant.status;
                        existingParticipant.type = participant.type;
                        existingParticipant.audioTransmitting = participant.audioTransmitting;
                        existingParticipant.audioReceivingFrom = participant.audioReceivingFrom;
                        existingParticipant.updatedOn = new Date();
                    }
                }));
            });

            VoxeetSDK.command.on('left', (participant, message) => {
                console.log('I LEFT');
            });
        },
        participants: [],
        isLoading: true,
        leave: () => {
            roomWatcher();
            messagesWatcher();
            filesWatcher();
            set(produce(state => {
                state.participants.length = 0;
                state.members = {};
                state.locked = false;
                if(state.files)
                    state.files.length = 0;
                if(state.messages)
                    state.messages.length = 0;
            }));
            VoxeetSDK.conference.leave({
                leaveRoom: true
            });
        },
        stopVideo: () => {
            VoxeetSDK.conference.stopVideo(VoxeetSDK.session.participant);
        },
        messages: [],
        sendMessage: (obj) => {
            VoxeetSDK.command.send(obj);
        },
        update: async (id, data) => {
            await (await request(`/api/room/${id}`, 'PUT', data)).json();
        },
        postMessage: async (data) => {
            const id = get().id;
            await (await request(`/api/room/${id}/message`, 'POST', data)).json();
        },
        postFile: async (data) => {
            const id = get().id;
            await (await request(`/api/room/${id}/file`, 'POST', data)).json();
        }
    }
});

export { useRoomStore };
